import React, { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import OutlinedInput from '@mui/material/OutlinedInput';

import { StyledFormControl, StyledInput } from './styles';

import { EMAIL_REGEX } from '../../../utils/validations';
import { useDebounce } from '../../../hooks';

const getInputAdornment = (adornment, position) =>
  adornment ? (
    <InputAdornment position={position}>
      {typeof adornment === 'string' ? (
        <Typography variant="inputAdornment">{adornment}</Typography>
      ) : (
        adornment
      )}
    </InputAdornment>
  ) : null;

export const VpHookFormInput = ({
  name = '',
  defaultValue = '',
  required = false,
  rules = {},
  disabled = false,
  isEmail = false,
  isNumber = false,
  maxLength = 300,
  onChange = null,
  onChangeDebounce = null,
  onChangeDebounceTimeout = 0,
  isLoading = false,
  placeholder = '',
  label = '',
  disableHelperText = false,
  helperText = '',
  startAdornment = null,
  endAdornment = null,
  smallLabel = false,
  hidden = false,
  isUppercase = false,
  isStandardInput = false,
  boldValue = false,
  fallbackDefaultValue = '',
  includeDisplayInput = false,
  ...rest
}) => {
  const id = `vp-hook-input-${name}`;
  const displayInputName = `${name}-display`;
  const inputStartAdornment = getInputAdornment(startAdornment, 'start');
  const inputEndAdornment = getInputAdornment(endAdornment, 'end');
  const vpInputProps = {
    ...(isLoading
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <CircularProgress size={24} />
            </InputAdornment>
          )
        }
      : {})
  };
  const vpOnChangeDebounce = onChangeDebounceTimeout
    ? useDebounce((val) => {
        onChangeDebounce(val);
      }, onChangeDebounceTimeout)
    : onChangeDebounce;

  const localRules = {
    ...(isEmail
      ? {
          pattern: {
            value: EMAIL_REGEX,
            message: 'Please enter a valid email address'
          }
        }
      : {}),
    ...(isNumber
      ? {
          pattern: {
            value: /^\d*$/,
            message: 'Please enter a valid number consisting of only digits (0-9)'
          }
        }
      : {})
  };

  const getFormattedValue = (valueToFormat) => {
    if (!valueToFormat || typeof valueToFormat !== 'string') {
      return valueToFormat;
    }
    if (isEmail) {
      return valueToFormat?.toLowerCase();
    }
    if (isUppercase) {
      return valueToFormat?.toUpperCase();
    }
    if (isNumber) {
      return valueToFormat.replace(/[^0-9.]/g, '');
    }

    return valueToFormat;
  };

  const fallbackValue = fallbackDefaultValue || '';
  const defaultInputValue = getFormattedValue(defaultValue) || fallbackValue;

  const { setValue, register } = useFormContext();
  const {
    field: { onChange: onControllerChange, onBlur, value, name: inputName, ref },
    fieldState: { error }
  } = useController({
    name: includeDisplayInput ? displayInputName : name,
    rules: { required, ...localRules, ...rules },
    defaultValue: defaultInputValue,
    shouldUnregister: true
  });

  useEffect(() => {
    if (includeDisplayInput) {
      setValue(name, value);
    }
  }, [value]);

  useEffect(() => {
    if (includeDisplayInput) {
      setValue(displayInputName, defaultInputValue);
    }
    setValue(name, defaultInputValue);
  }, [defaultInputValue]);

  const errorMessage = error?.message;
  // const defaultHelperText = disableHelperText ? '' : <span>&nbsp;</span>;
  const helperTextMessage = disableHelperText
    ? null
    : errorMessage || helperText || <span>&nbsp;</span>;

  const onTextFieldChange = (e) => {
    onControllerChange(e);
    onChange && onChange(e);
    vpOnChangeDebounce && vpOnChangeDebounce(e);
  };

  const inputValue = getFormattedValue(value);

  const InputElement = isStandardInput ? StyledInput : OutlinedInput;
  const inputStyleProps = boldValue ? { boldValue } : {};

  return (
    <StyledFormControl
      hidden={hidden}
      fullWidth
      variant={isStandardInput ? 'standard' : 'outlined'}
    >
      {label && (
        <Typography htmlFor={id} variant={smallLabel ? 'inputLabelSmall' : 'inputLabel'}>
          {label}
        </Typography>
      )}
      <InputElement
        id={id}
        onChange={onTextFieldChange}
        onBlur={onBlur}
        value={inputValue}
        inputRef={ref}
        name={inputName}
        disabled={disabled}
        size="small"
        error={!!error}
        inputProps={{
          maxLength
        }}
        placeholder={placeholder}
        startAdornment={inputStartAdornment}
        endAdornment={inputEndAdornment}
        {...inputStyleProps}
        {...vpInputProps}
        {...rest}
      />
      {helperTextMessage && (
        <FormHelperText error={!!error} id={`${id}-helper-text`}>
          {helperTextMessage}
        </FormHelperText>
      )}
      {includeDisplayInput && (
        <input {...register(name)} type="hidden" defaultValue={defaultValue} />
      )}
    </StyledFormControl>
  );
};

VpHookFormInput.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEmail: PropTypes.bool,
  isNumber: PropTypes.bool,
  onChange: PropTypes.func,
  onChangeDebounce: PropTypes.func,
  onChangeDebounceTimeout: PropTypes.number,
  isLoading: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  disableHelperText: PropTypes.bool,
  helperText: PropTypes.string,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  smallLabel: PropTypes.bool,
  hidden: PropTypes.bool,
  isUppercase: PropTypes.bool,
  isStandardInput: PropTypes.bool,
  boldValue: PropTypes.bool,
  fallbackDefaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  includeDisplayInput: PropTypes.bool
};

export default VpHookFormInput;



// WEBPACK FOOTER //
// ./src/vp-ui/inputs/VpHookFormInput/VpHookFormInput.jsx